import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tabs,
  Tab,
  Box,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { Type } from "../../../../redux/types/types";
import { Language } from "../../../LanguageProvider/LanguageProvider";

interface EditTypeDialogProps {
  open: boolean;
  typeToEdit: Type | null;
  onClose: () => void;
  onSave: (
    imageFile?: File | null,
    subtypeImageFiles?: Array<File | null>,
    removeCurrentImage?: boolean
  ) => void;
  onTypeChange: (updatedType: Type) => void;
}

export default function EditTypeDialog({
  open,
  typeToEdit,
  onClose,
  onSave,
  onTypeChange,
}: EditTypeDialogProps) {
  const intl = useIntl();
  const [currentTab, setCurrentTab] = useState<"en" | "uk" | "fr">(
    process.env.REACT_APP_DEFAULT_LANGUAGE as "en" | "uk" | "fr"
  );
  const [subtypeImageFiles, setSubtypeImageFiles] = useState<Array<File | null>>(
    []
  );
  const [subtypeImagePreviews, setSubtypeImagePreviews] = useState<
    Array<string | null>
  >([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [removeCurrentImage, setRemoveCurrentImage] = useState(false);

  useEffect(() => {
    setSubtypeImageFiles(typeToEdit?.subtypes.map(() => null) || []);
    setSubtypeImagePreviews(typeToEdit?.subtypes.map(() => null) || []);
    setImageFile(null);
    setImagePreview(null);
    setRemoveCurrentImage(false);
  }, [typeToEdit]);

  const handleChange =
    (language: keyof Type["name"]) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (typeToEdit) {
        const updatedType: Type = {
          ...typeToEdit,
          name: {
            ...typeToEdit.name,
            [language]: e.target.value,
          },
        };
        onTypeChange(updatedType);
      }
    };

  const handleSubtypeChange =
    (index: number, language: keyof Type["name"]) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!typeToEdit) {
        return;
      }

      onTypeChange({
        ...typeToEdit,
        subtypes: typeToEdit.subtypes.map((subtype, subtypeIndex) =>
          subtypeIndex === index
            ? {
                ...subtype,
                name: {
                  ...subtype.name,
                  [language]: e.target.value,
                },
              }
            : subtype
        ),
      });
    };

  const handleSubtypeImageChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;

      setSubtypeImageFiles((prev) => {
        const next = [...prev];
        next[index] = file;
        return next;
      });

      setSubtypeImagePreviews((prev) => {
        const next = [...prev];
        next[index] = file ? URL.createObjectURL(file) : null;
        return next;
      });
    };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setImageFile(file);
    setImagePreview(file ? URL.createObjectURL(file) : null);
    setRemoveCurrentImage(false);
  };

  if (!typeToEdit) {
    return null;
  }

  const isDefaultLangEmpty =
    typeToEdit.name[
      process.env.REACT_APP_DEFAULT_LANGUAGE as Language
    ].trim() === "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="form-dialog-title">
        <FormattedMessage id="admin.dashboard.editType" />
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={currentTab}
          onChange={(_, newValue: "en" | "uk" | "fr") =>
            setCurrentTab(newValue)
          }
          aria-label="language tabs"
        >
          <Tab
            label={intl.formatMessage({
              id:
                (process.env.REACT_APP_DEFAULT_LANGUAGE === "en" &&
                  "common.english") ||
                (process.env.REACT_APP_DEFAULT_LANGUAGE === "fr" &&
                  "common.french") ||
                "",
            })}
            value={process.env.REACT_APP_DEFAULT_LANGUAGE}
          />
          <Tab
            label={
              isDefaultLangEmpty ? (
                <Tooltip
                  title={intl.formatMessage(
                    {
                      id: "admin.dashboard.fillDefaultFirst",
                    },
                    {
                      default:
                        (process.env.REACT_APP_DEFAULT_LANGUAGE === "en" &&
                          intl.formatMessage({ id: "common.english" })) ||
                        (process.env.REACT_APP_DEFAULT_LANGUAGE === "fr" &&
                          intl.formatMessage({ id: "common.french" })) ||
                        "",
                    }
                  )}
                >
                  <span>{intl.formatMessage({ id: "common.ukrainian" })}</span>
                </Tooltip>
              ) : (
                intl.formatMessage({ id: "common.ukrainian" })
              )
            }
            value="uk"
            disabled={isDefaultLangEmpty}
            sx={
              isDefaultLangEmpty
                ? {
                    pointerEvents: "auto",
                    "&.Mui-disabled": {
                      pointerEvents: "auto",
                    },
                    "& .MuiTab-wrapper": {
                      pointerEvents: "none",
                    },
                  }
                : undefined
            }
          />
          <Tab
            label={
              isDefaultLangEmpty ? (
                <Tooltip
                  title={intl.formatMessage(
                    {
                      id: "admin.dashboard.fillDefaultFirst",
                    },
                    {
                      default:
                        (process.env.REACT_APP_DEFAULT_LANGUAGE === "en" &&
                          intl.formatMessage({ id: "common.english" })) ||
                        (process.env.REACT_APP_DEFAULT_LANGUAGE === "fr" &&
                          intl.formatMessage({ id: "common.french" })) ||
                        "",
                    }
                  )}
                >
                  <span>
                    {intl.formatMessage({
                      id:
                        (process.env.REACT_APP_DEFAULT_LANGUAGE === "en" &&
                          "common.french") ||
                        (process.env.REACT_APP_DEFAULT_LANGUAGE === "fr" &&
                          "common.english") ||
                        "",
                    })}
                  </span>
                </Tooltip>
              ) : (
                intl.formatMessage({
                  id:
                    (process.env.REACT_APP_DEFAULT_LANGUAGE === "en" &&
                      "common.french") ||
                    (process.env.REACT_APP_DEFAULT_LANGUAGE === "fr" &&
                      "common.english") ||
                    "",
                })
              )
            }
            value={
              (process.env.REACT_APP_DEFAULT_LANGUAGE === "fr" && "en") ||
              (process.env.REACT_APP_DEFAULT_LANGUAGE === "en" && "fr")
            }
            disabled={isDefaultLangEmpty}
            sx={
              isDefaultLangEmpty
                ? {
                    pointerEvents: "auto",
                    "&.Mui-disabled": {
                      pointerEvents: "auto",
                    },
                    "& .MuiTab-wrapper": {
                      pointerEvents: "none",
                    },
                  }
                : undefined
            }
          />
        </Tabs>
        <Box sx={{ mt: 2 }}>
          {currentTab === "en" && (
            <TextField
              margin="dense"
              id="name-en"
              label={intl.formatMessage({ id: "admin.dashboard.nameEn" })}
              type="text"
              fullWidth
              value={typeToEdit.name.en}
              onChange={handleChange("en")}
            />
          )}
          {currentTab === "uk" && (
            <TextField
              margin="dense"
              id="name-uk"
              label={intl.formatMessage({ id: "admin.dashboard.nameUk" })}
              type="text"
              fullWidth
              value={typeToEdit.name.uk}
              onChange={handleChange("uk")}
            />
          )}
          {currentTab === "fr" && (
            <TextField
              margin="dense"
              id="name-fr"
              label={intl.formatMessage({ id: "admin.dashboard.nameFr" })}
              type="text"
              fullWidth
              value={typeToEdit.name.fr}
              onChange={handleChange("fr")}
            />
          )}
        </Box>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={typeToEdit.isDisplayed}
              onChange={(e) =>
                onTypeChange({
                  ...typeToEdit,
                  isDisplayed: e.target.checked,
                })
              }
            />
          }
          label={intl.formatMessage({ id: "admin.dashboard.isDisplayed" })}
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            <FormattedMessage id="admin.dashboard.images" />
          </Typography>
          <Button variant="outlined" component="label" startIcon={<AddIcon />}>
            <FormattedMessage id="admin.dashboard.clickToUpload" />
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
          </Button>
          {imagePreview || (typeToEdit.image && !removeCurrentImage) ? (
            <Box
              sx={{ mt: 2, position: "relative", width: "100%", maxWidth: 260 }}
            >
              <Box
                component="img"
                src={
                  imagePreview ||
                  `${process.env.REACT_APP_API_URL}/${typeToEdit.image}`
                }
                alt="Classification preview"
                sx={{
                  width: "100%",
                  maxHeight: 220,
                  objectFit: "contain",
                  borderRadius: 1,
                  display: "block",
                }}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  backgroundColor: "rgba(255,255,255,0.8)",
                }}
                onClick={() => {
                  setImageFile(null);
                  setImagePreview(null);
                  setRemoveCurrentImage(Boolean(typeToEdit.image));
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ) : null}
        </Box>
        <Box sx={{ mt: 2 }}>
          {typeToEdit.subtypes.map((subtype, index) => (
            <Box key={subtype.id || index} sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  margin="dense"
                  label={`${intl.formatMessage({
                    id: "admin.dashboard.subtype",
                  })} ${index + 1}`}
                  type="text"
                  fullWidth
                  value={subtype.name[currentTab]}
                  onChange={handleSubtypeChange(index, currentTab)}
                />
                <Button
                  color="error"
                  onClick={() => {
                    onTypeChange({
                      ...typeToEdit,
                      subtypes: typeToEdit.subtypes.filter(
                        (_, subtypeIndex) => subtypeIndex !== index
                      ),
                    });
                    setSubtypeImageFiles((prev) =>
                      prev.filter((_, subtypeIndex) => subtypeIndex !== index)
                    );
                    setSubtypeImagePreviews((prev) =>
                      prev.filter((_, subtypeIndex) => subtypeIndex !== index)
                    );
                  }}
                >
                  <FormattedMessage id="admin.dashboard.delete" />
                </Button>
              </Box>
              <Button variant="outlined" component="label" startIcon={<AddIcon />}>
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={handleSubtypeImageChange(index)}
                />
                <FormattedMessage id="admin.dashboard.clickToUpload" />
              </Button>
              {subtypeImagePreviews[index] || subtype.image ? (
                <Box
                  sx={{
                    mt: 1,
                    position: "relative",
                    width: "100%",
                    maxWidth: 220,
                  }}
                >
                  <Box
                    component="img"
                    src={
                      subtypeImagePreviews[index] ||
                      `${process.env.REACT_APP_API_URL}/${subtype.image}`
                    }
                    alt={`Subtype ${index + 1} preview`}
                    sx={{
                      width: "100%",
                      maxHeight: 180,
                      objectFit: "contain",
                      borderRadius: 1,
                      display: "block",
                    }}
                  />
                  {subtypeImagePreviews[index] ? (
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        backgroundColor: "rgba(255,255,255,0.8)",
                      }}
                      onClick={() => {
                        setSubtypeImageFiles((prev) => {
                          const next = [...prev];
                          next[index] = null;
                          return next;
                        });
                        setSubtypeImagePreviews((prev) => {
                          const next = [...prev];
                          next[index] = null;
                          return next;
                        });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          ))}
          <Button
            onClick={() => {
              onTypeChange({
                ...typeToEdit,
                subtypes: [
                  ...typeToEdit.subtypes,
                  { id: 0, name: { en: "", uk: "", fr: "" }, image: null },
                ],
              });
              setSubtypeImageFiles((prev) => [...prev, null]);
              setSubtypeImagePreviews((prev) => [...prev, null]);
            }}
          >
            <FormattedMessage id="admin.dashboard.addSubtype" />
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          <FormattedMessage id="admin.dashboard.cancel" />
        </Button>
        <Button
          onClick={() => onSave(imageFile, subtypeImageFiles, removeCurrentImage)}
          color="primary"
        >
          <FormattedMessage id="admin.dashboard.save" />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
